/**
 * LluviaRepository - Repositorio unificado para Lluvias (KAN-87)
 *
 * Implementación compartida entre web (Dexie) y mobile (SQLite).
 *
 * DIFERENCIA con RecordatorioRepository: delete() es SOFT-delete
 * (marca is_deleted=true y emite el UPDATE de sync), porque `lluvias`
 * tiene la columna is_deleted en Supabase y así los borrados se
 * propagan offline vía UPDATE (mismo modelo que gastos/ubicaciones).
 */

import { v4 as uuidv4 } from 'uuid';
import { ILluviaRepository, Lluvia, FiltroLluvias } from '../interfaces/ILluviaRepository';
import { BaseRepository } from './BaseRepository';
import { IDatabaseAdapter, WhereClause } from '../adapters/IDatabaseAdapter';
import { IProfileRepository } from '../interfaces/IProfileRepository';

/**
 * Repositorio unificado de Lluvias
 */
export class LluviaRepository extends BaseRepository<Lluvia, string> implements ILluviaRepository {
  private profileRepo: IProfileRepository | null = null;

  /** Clause reutilizable para excluir soft-deletes (is_deleted=false). */
  private readonly NOT_DELETED: WhereClause = { field: 'is_deleted', operator: 'equals', value: false };

  constructor(db: IDatabaseAdapter, profileRepo?: IProfileRepository) {
    super(db, 'lluvias', 'lluvias');

    // Habilitar funcionalidades de BaseRepository
    this.supportsSoftDelete = true;
    this.autoTimestamp = true;

    if (profileRepo) {
      this.profileRepo = profileRepo;
      this.setProfileRepository(profileRepo);
    }
  }

  async add(data: Omit<Lluvia, 'id'>): Promise<string> {
    const id = uuidv4();
    const now = this.getCurrentTimestamp();
    const profile = await this.getCurrentProfile();

    const record: Lluvia = {
      ...data,
      id,
      user_id: data.user_id || profile?.user_id || '',
      empresa_id: data.empresa_id || profile?.empresa_activa_id || '',
      created_at: data.created_at || now,
      updated_at: now,
      is_deleted: false
    };

    await this.db.insert('lluvias', record);
    this.emitSync('INSERT', record);
    return id;
  }

  async update(data: Partial<Lluvia> & { id: string }): Promise<void> {
    const updatedData = {
      ...data,
      updated_at: this.getCurrentTimestamp()
    };
    await this.db.update('lluvias', data.id, updatedData);
    this.emitSync('UPDATE', updatedData as any);
  }

  /**
   * Soft-delete: marca is_deleted=true en lugar de borrar la fila.
   * El UPDATE viaja por la cola de sync y el borrado se propaga a
   * Supabase (y al resto de los dispositivos) aunque se haga offline.
   */
  async delete(id: string): Promise<void> {
    await this.softDelete(id);
  }

  async getByFiltros(filtros: FiltroLluvias): Promise<Lluvia[]> {
    const clauses: WhereClause[] = [this.NOT_DELETED];

    if (filtros.fecha_inicio) {
      clauses.push({
        field: 'fecha',
        operator: 'greaterThanOrEqual',
        value: filtros.fecha_inicio
      });
    }

    if (filtros.fecha_fin) {
      clauses.push({
        field: 'fecha',
        operator: 'lessThanOrEqual',
        value: filtros.fecha_fin
      });
    }

    if (filtros.ubicacion_id) {
      clauses.push({
        field: 'ubicacion_id',
        operator: 'equals',
        value: filtros.ubicacion_id
      });
    }

    if (filtros.empresa_id) {
      clauses.push({
        field: 'empresa_id',
        operator: 'equals',
        value: filtros.empresa_id
      });
    }

    return this.db.queryMultiple<Lluvia>('lluvias', clauses, {
      column: 'fecha',
      direction: 'desc'
    });
  }

  async getByEmpresa(empresaId: string): Promise<Lluvia[]> {
    return this.db.queryMultiple<Lluvia>('lluvias', [
      this.NOT_DELETED,
      { field: 'empresa_id', operator: 'equals', value: empresaId }
    ], { column: 'fecha', direction: 'desc' });
  }

  async getByPeriodo(fechaInicio: string, fechaFin: string): Promise<Lluvia[]> {
    return this.db.queryMultiple<Lluvia>('lluvias', [
      this.NOT_DELETED,
      {
        field: 'fecha',
        operator: 'between',
        value: [fechaInicio, fechaFin]
      }
    ], { column: 'fecha', direction: 'desc' });
  }

  async getByUbicacion(ubicacionId: string): Promise<Lluvia[]> {
    return this.db.queryMultiple<Lluvia>('lluvias', [
      this.NOT_DELETED,
      { field: 'ubicacion_id', operator: 'equals', value: ubicacionId }
    ], { column: 'fecha', direction: 'desc' });
  }

  setProfileRepository(profileRepo: IProfileRepository): void {
    this.profileRepo = profileRepo;
    super.setProfileRepository(profileRepo);
  }
}
